/**
 * Wrappers de GSAP.
 *
 * `useGSAP()` cuida do cleanup ao desmontar — sem ele, animação em lista que
 * muita hora troca de item vaza timeline. Tudo aqui respeita
 * `prefers-reduced-motion`: quem pediu menos movimento vê o conteúdo no
 * estado final, sem animação, em vez de ver a tela tremer.
 */
import { useRef, type ReactNode } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { formatarBRL } from '@tcm/shared';

export function movimentoReduzido(): boolean {
  return typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Entrada suave de um bloco. */
export function FadeIn({
  children,
  delay = 0,
  className,
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) {
  const alvo = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      if (movimentoReduzido()) return;
      gsap.from(alvo.current, {
        opacity: 0,
        y: 16,
        duration: 0.45,
        delay,
        ease: 'power2.out',
      });
    },
    { scope: alvo, dependencies: [delay] },
  );

  return (
    <div ref={alvo} className={className}>
      {children}
    </div>
  );
}

/**
 * Entrada em cascata dos filhos diretos — é o efeito das bolinhas do monitor
 * aparecendo uma depois da outra.
 */
export function Cascata({
  children,
  className,
  seletor = ':scope > *',
  chave,
}: {
  children: ReactNode;
  className?: string;
  seletor?: string;
  /** Muda quando a lista muda, para reanimar. */
  chave?: string | number;
}) {
  const alvo = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      if (movimentoReduzido()) return;
      const itens = alvo.current?.querySelectorAll(seletor);
      if (!itens || itens.length === 0) return;

      gsap.from(itens, {
        opacity: 0,
        y: 12,
        scale: 0.96,
        duration: 0.4,
        stagger: 0.035,
        ease: 'power2.out',
      });
    },
    { scope: alvo, dependencies: [chave] },
  );

  return (
    <div ref={alvo} className={className}>
      {children}
    </div>
  );
}

/**
 * Contador numérico que sobe do zero.
 * Em `centavos`, formata como moeda; senão, número inteiro.
 */
export function Contador({
  valor,
  centavos = false,
  className,
}: {
  valor: number;
  centavos?: boolean;
  className?: string;
}) {
  const alvo = useRef<HTMLSpanElement>(null);

  useGSAP(
    () => {
      const el = alvo.current;
      if (!el) return;

      const escrever = (n: number): void => {
        el.textContent = centavos ? formatarBRL(Math.round(n)) : String(Math.round(n));
      };

      if (movimentoReduzido()) {
        escrever(valor);
        return;
      }

      const estado = { n: 0 };
      gsap.to(estado, {
        n: valor,
        duration: 0.9,
        ease: 'power2.out',
        onUpdate: () => escrever(estado.n),
      });
    },
    { dependencies: [valor, centavos] },
  );

  return (
    <span ref={alvo} className={className}>
      {centavos ? formatarBRL(valor) : valor}
    </span>
  );
}

/** Barra que cresce do zero — as barras por categoria do dashboard. */
export function BarraProgresso({
  percentual,
  cor,
  className,
}: {
  percentual: number;
  cor: string;
  className?: string;
}) {
  const alvo = useRef<HTMLDivElement>(null);
  const limitado = Math.max(0, Math.min(100, percentual));

  useGSAP(
    () => {
      if (movimentoReduzido()) return;
      gsap.from(alvo.current, { width: 0, duration: 0.8, ease: 'power2.out' });
    },
    { dependencies: [limitado] },
  );

  return (
    <div className={`h-2 w-full rounded-full bg-slate-200 overflow-hidden ${className ?? ''}`}>
      <div
        ref={alvo}
        className="h-full rounded-full"
        style={{ width: `${limitado}%`, backgroundColor: cor }}
        role="presentation"
      />
    </div>
  );
}

/** Sacode o elemento no erro de validação. */
export function useTremor() {
  return (elemento: HTMLElement | null): void => {
    if (!elemento || movimentoReduzido()) return;
    gsap.fromTo(
      elemento,
      { x: -6 },
      { x: 0, duration: 0.4, ease: 'elastic.out(1, 0.3)' },
    );
  };
}
